<template>
  <div
    class="pb-beams"
    :class="{ loading }"
  >
    <div class="pb-beams-grid">
      <div
        v-for="[rowPosition, row] in rows"
        :key="rowPosition"
        class="pb-beams-row"
      >
        <template
          v-for="cell in row.cells"
          :key="`row-${cell.row}-column-${cell.column}`"
        >
          <BeamsCell
            :mode="mode"
            :beam="cell.beam"
            :loading="loading"
            :disabled="disabled"
            @change="change(cell, $event)"
          />
        </template>
      </div>
    </div>
    <div
      v-if="loading"
      class="pb-beams-loader"
    >
      <div class="pb-beams-loader-spinner">
        <Spinner />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
// name: 'BeamsGrid'

import { computed } from 'vue'
import Spinner from '@nicolaudie-group/ng-ui-kit/components/Spinner/Spinner.vue'
import BeamsCell from './BeamsCell.vue'
import type { Beam, Cell } from './beamsTypes.ts'

interface Props {
  mode?: 'edit' | 'select'
  rowsCount?: number
  columnsCount?: number
  beams?: Beam[]
  loading?: boolean
  disabled?: boolean
}

/**
 * Represents a column or row of cells
 */
interface GridLine {
  position: number
  cells: Cell[]
}

defineOptions({
  compatConfig: {
    MODE: 3, // opt-in to Vue 3 behavior for this component only
  },
})

const props = withDefaults(defineProps<Props>(), {
  mode: 'edit',
  rowsCount: 1,
  columnsCount: 1,
  beams: () => [],
  loading: false,
  disabled: false,
})

const emit = defineEmits<{
  change: [cell: Cell, cellChecked: boolean]
}>()

/**
 * Map the beams by their position: "pos_x/pos_y" => beam
 */
const beamsMap = computed(() => new Map(
  props.beams.map((beam) => ([
    `${beam.pos_x}/${beam.pos_y}`,
    beam,
  ])
)))

function getBeamAtPosition(row: number, column: number) {
  return beamsMap.value.get(`${column}/${row}`)
}

function change(cell: Cell, cellChecked: boolean) {
  emit('change', cell, cellChecked)
}

/**
 * Create and organize cells based on rows and columns, each cell may contain a beam
 */
const organized = computed(() =>  {
  const cells: Cell[] = []
  const rows: Map<number, GridLine> = new Map([[0, { position: 0, cells: [] }]])
  const columns: Map<number, GridLine> = new Map([[0, { position: 0, cells: [] }]])

  // For each column of each row
  for (let rowPosition = 0; rowPosition < props.rowsCount * 100; rowPosition += 100) {
    for (let columnPosition = 0; columnPosition < props.columnsCount * 100; columnPosition += 100) {
      // Create a cell
      const cell = {
        row: rowPosition,
        column: columnPosition,
        beam: getBeamAtPosition(rowPosition, columnPosition),
      }
      cells.push(cell)

      const row = rows.get(rowPosition) ?? { position: rowPosition, cells: [] }
      const column = columns.get(columnPosition) ?? { position: columnPosition, cells: [] }

      // Store row/column in case it was just created
      rows.set(rowPosition, row)
      columns.set(columnPosition, column)

      // Add the beam to the row/column
      row.cells.push(cell)
      column.cells.push(cell)
    }
  }

  return {
    cells,
    rows,
    columns,
  }
})

const rows = computed(() => {
  return organized.value.rows
})
</script>

<style lang="scss" src="./BeamsGrid.scss" />
